"use client"

import { NearIcon, NearLogoIcon } from "@src/icons"
import clsx from "clsx"
import Link from "next/link"
import { usePathname } from "next/navigation"
import type { ReactNode } from "react"
import { LEGAL_ROUTES } from "./legalRoutes"
import { MarketingHeader } from "./MarketingHeader"

const links = {
  Product: [
    {
      name: "Dashboard",
      href: "/home",
    },
    {
      name: "Support",
      onClick: () => {
        if (typeof window !== "undefined" && window.Beacon) {
          window.Beacon("navigate", "/answers/")
          window.Beacon("open")
        }
      },
    },
  ],
  Social: [
    {
      name: "X/Twitter",
      href: "https://x.com/nearprotocol",
    },
    {
      name: "Discord",
      href: "https://discord.gg/nearprotocol",
    },
  ],
  Legal: [
    {
      name: "Terms",
      link: "/terms",
    },
    {
      name: "Privacy",
      link: "/privacy",
    },
  ],
}

function FooterLink({
  item,
}: {
  item: (typeof links)[keyof typeof links][number]
}) {
  const className =
    "font-medium text-gray-400 text-base/6 hover:text-white hover:underline"

  if ("onClick" in item) {
    return (
      <button type="button" onClick={item.onClick} className={className}>
        {item.name}
      </button>
    )
  }

  const href = "href" in item ? item.href : item.link

  if (href.startsWith("http")) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {item.name}
      </a>
    )
  }

  return (
    <Link href={href} className={className}>
      {item.name}
    </Link>
  )
}

// Client-side route check on purpose: reading headers here would deopt the
// ISR landing page. Only legal routes go dark; the rest stay light.
export function MarketingFrame({ children }: { children: ReactNode }) {
  const pathname = usePathname()
  const isLegal = LEGAL_ROUTES.has(pathname)
  const isHomePage = pathname === "/"
  const surface = clsx("bg-white", isLegal && "dark:bg-gray-850")

  return (
    <div
      className={clsx(
        "flex min-h-screen flex-col",
        !isLegal && "not-dark",
        surface
      )}
    >
      <MarketingHeader />

      <main className="flex flex-1 flex-col">
        {children}
        <div className={clsx("grow", surface)} />
      </main>

      <footer className="relative overflow-hidden bg-gray-800">
        {isHomePage && (
          <div className="-top-4 -translate-x-1/2 pointer-events-none absolute left-1/2 flex h-8 w-[max(200%,100vw)] select-none items-center justify-center bg-green-500 opacity-20 blur-xl" />
        )}

        <div className="mx-auto w-full max-w-7xl border-white/11 border-x max-xl:border-none">
          <div className="grid gap-y-16 px-6 py-24 sm:px-8 lg:grid-cols-2">
            <div>
              <div className="flex items-center gap-3">
                <NearIcon className="size-7 shrink-0" />
                <NearLogoIcon className="h-3.5 shrink-0 text-white" />
              </div>

              <p className="mt-5 text-balance font-medium text-base/6 text-gray-400">
                One borderless account for crypto, stocks, bonds, precious
                metals, yield, and more—confidential by design.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-y-16 sm:grid-cols-3">
              {Object.entries(links).map(([title, items]) => (
                <div key={title}>
                  <h3 className="font-medium text-base/6 text-white">
                    {title}
                  </h3>

                  <ul className="mt-6 space-y-4">
                    {items.map((item) => (
                      <li key={item.name}>
                        <FooterLink item={item} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="-mb-[calc(30%*314/1214)] sm:-mb-[calc(34%*314/1214)] lg:-mb-[calc(38%*314/1214)] w-full px-6 sm:px-8">
            <svg
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 1214 314"
            >
              <g clipPath="url(#a)">
                <path
                  fill="url(#b)"
                  stroke="url(#c)"
                  d="M477.085.5273h1.205c88.384 0 149.759 56.6551 149.759 127.4737v37.667c0 1.191-.235 2.37-.692 3.471a9.06 9.06 0 0 1-1.967 2.941 9.08 9.08 0 0 1-2.946 1.966 9.07 9.07 0 0 1-3.473.69H426.203c-7.614 0-12.667-4.655-12.667-9.661v-.594c0-5.722 4.017-10.309 9.177-11.463l134.218-26.022-.001-.001c2.23-.431 4.225-1.665 5.602-3.471a9.23 9.23 0 0 0 1.867-6.317v-.008l-.001-.008-.105-.903c-4.749-38.1002-44.882-61.1433-85.99-61.1435-22.841 0-44.799 6.9048-61.132 18.8584-16.337 11.9566-27.068 28.9841-27.368 49.2061v.002l-.607 53.219c-.598 48.188 42.804 81.837 89.107 81.837 46.3-.001 74.633-28.833 80.692-48.193l.004-.011c1.133-3.943 4.509-6.214 8.503-6.214h46.691c2.878 0 5.288 1.002 6.903 2.683 1.611 1.678 2.468 4.068 2.178 6.926-5.952 57.054-63.101 99.427-144.971 99.427-46.295 0-84.181-13.874-110.487-37.845-26.301-23.967-41.067-58.067-41.067-98.604v-42.464c0-38.4359 14.762-71.7882 41.066-95.5355C393.893 14.8941 431.349.7657 477.091.5205zM812.335.5c41.807.0051 75.044 11.4919 97.824 31.5703 22.779 20.077 35.151 48.7871 35.152 83.3427v181.206c-.001 5.101-3.972 9.069-9.08 9.069h-43.102c-2.517-.001-4.281-1.246-5.09-2.93-.813-1.693-.691-3.891.708-5.849l.007-.011.008-.01 17.941-28.089c1.534-2.14 2.627-4.615 2.863-7.042.238-2.442-.393-4.842-2.31-6.757h.001c-2.16-2.164-4.524-3.289-7.008-3.289-2.322 0-4.67.984-6.977 2.876l-.456.387-29.339 25.118c-19.666 16.666-51.283 30.972-88.271 30.972-28.959 0-56.091-7.008-75.966-20.689-19.859-13.67-32.484-34.004-32.484-60.734v-8.37c0-26.115 14.108-48.104 35.266-63.582 21.162-15.48 49.344-24.418 77.379-24.418h66.443c2.326 0 4.781.581 6.712 1.726 1.925 1.141 3.297 2.818 3.57 5.017l.006.037c.582 2.896.283 5.612-.921 7.804-1.2 2.185-3.327 3.903-6.504 4.768l-64.039 17.34c-22.183 5.09-36.053 11.995-44.379 20.237-8.347 8.263-11.073 17.824-11.073 28.092 0 11.85 5.639 21.423 16.098 28.011 10.437 6.573 25.658 10.169 44.867 10.169 24.632 0 46.907-6.751 63.033-20.225 16.138-13.483 26.072-33.662 26.072-60.407v-52.032c0-21.639-6.924-38.8214-19.012-50.5941-12.085-11.7707-29.283-18.084-49.737-18.084-17.148.0001-33.703 4.8082-46.583 12.9336-12.877 8.1237-22.119 19.5911-24.546 32.9195l-.004.028c-.579 4.568-3.984 7.931-8.488 7.931h-48.485c-5.176 0-9.079-3.425-9.079-7.869 0-28.5007 16.045-53.6288 41.466-71.6511C740.241 11.3992 775.008.5127 812.332.5127zm191.545 7.1943h44.9c2.52 0 4.28 1.2452 5.09 2.9297.81 1.6924.69 3.8912-.71 5.8487v.0058l-.01.0069-19.15 28.705c-3.67 4.8883-1.98 10.9331 2.11 14.1416l.4.3028c1.93 1.5955 4.33 1.8955 6.59 1.5068 2.26-.3898 4.43-1.4768 5.99-2.7227l.01-.0058.01-.0059 26.33-22.7197c16.14-13.7406 38.78-27.9932 61.93-27.9932h67.06c5.11 0 9.08 3.9677 9.08 9.0684v37.6807c0 5.1008-3.97 9.0683-9.08 9.0683h-62.26c-27.31 0-47.38 6.8977-60.61 21.6192-13.21 14.7096-19.51 37.1341-19.51 67.9871v143.526c0 5.1-3.97 9.068-9.08 9.068h-49.09c-5.106 0-9.077-3.968-9.077-9.068V16.7627c0-5.1007 3.971-9.0684 9.077-9.0684ZM168.814 1.712c35.219.001 63.669 10.4348 83.312 29.1631 19.641 18.7267 30.529 45.7966 30.529 79.174v186.582c0 5.101-3.97 9.068-9.078 9.068h-47.891c-5.108 0-9.08-3.967-9.08-9.068v-180.6c0-13.253-4.371-28.6247-14.864-40.6902-10.505-12.0795-27.109-20.8047-51.483-20.8047-11.434 0-32.344 2.8523-50.4113 14.8086C81.7479 81.3225 66.549 102.409 66.5488 138.753v157.878c0 5.101-3.9715 9.068-9.0791 9.068H9.5781C4.4707 305.699.5 301.732.5 296.631V16.751c0-5.1009 3.9707-9.0683 9.0781-9.0684h46.0977c2.5171 0 4.2808 1.2443 5.0898 2.9287.8128 1.6925.6909 3.8911-.708 5.8487l-.0039.0068-.0049.0068L40.8994 45.167c-1.8441 2.4628-2.6465 4.9732-2.4072 7.3652.2399 2.397 1.5185 4.6001 3.6914 6.4571 2.2229 1.9111 4.9178 2.3785 7.416 2.0673 2.4898-.3101 4.822-1.3958 6.3887-2.6504l.0078-.0058.0068-.0068 35.9248-31.0909.0039-.0029C112.163 9.4628 135.978 1.713 168.814 1.7119Z"
                />
              </g>
              <defs>
                <linearGradient
                  id="b"
                  x1="-68.5"
                  x2="1347.5"
                  y1="-7.917"
                  y2="222.083"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#fff" stopOpacity=".02" />
                  <stop offset="1" stopColor="#fff" stopOpacity=".01" />
                </linearGradient>
                <linearGradient
                  id="c"
                  x1="-85"
                  x2="1347"
                  y1="-17.417"
                  y2="239.083"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#fff" stopOpacity=".05" />
                  <stop offset=".5" stopColor="#fff" stopOpacity=".04" />
                  <stop offset="1" stopColor="#fff" stopOpacity=".02" />
                </linearGradient>
                <clipPath id="a">
                  <path fill="#fff" d="M0 0h1214v314H0z" />
                </clipPath>
              </defs>
            </svg>
          </div>
        </div>
      </footer>
    </div>
  )
}
